import React, { useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import CreditEligiblePill from 'components/common/CreditEligiblePill';
import type { Ota } from 'api/types';

export interface OtaViewModalProps {
  isOpen: boolean;
  ota: Ota | null;
  onClose: () => void;
  onEdit?: (ota: Ota) => void;
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (otaId: string) => void;
}

const ReadOnlyField: React.FC<{ label: string; value?: string | number | boolean | null; span?: boolean }> = ({ label, value, span }) => (
  <div style={span ? { gridColumn: '1 / -1' } : undefined}>
    <label className="form-label">{label}</label>
    <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--form-border-color)' }}>
      {value != null && value !== '' ? (typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)) : '-'}
    </div>
  </div>
);

const OtaViewModal: React.FC<OtaViewModalProps> = ({
  isOpen, ota, onClose, onEdit, tabId, moduleName, onRestoreView,
}) => {
  const { minimize } = useModalMinimize();

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !ota) return;
    minimize({
      id: `${tabId}-view-modal-${ota.id}`,
      title: `View - ${ota.otaCode}`,
      moduleName,
      tabId,
      onRestore: () => onRestoreView?.(ota.id),
    });
    onClose();
  }, [tabId, moduleName, ota, minimize, onClose, onRestoreView]);

  if (!isOpen || !ota) return null;

  const formatNumber = (val?: number) => val != null ? String(val) : '-';
  const formatPercent = (val?: number) => val != null ? `${val}%` : '-';
  const formatCurrency = (val?: number) => val != null ? val.toFixed(2) : '-';

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto' }}>
        <div className="modal-header">
          <h2 className="modal-title">OTA Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem' }}>
            <ReadOnlyField label="OTA Code" value={ota.otaCode} />
            <ReadOnlyField label="OTA Name" value={ota.otaName} />
            <div>
              <label className="form-label">Status</label>
              <div>
                <span className={`badge badge-${ota.isActive ? 'active' : 'inactive'}`}>
                  {ota.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem' }}>Contact Information</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem' }}>
              <ReadOnlyField label="Contact Person" value={ota.contactPerson} />
              <ReadOnlyField label="Phone" value={ota.phone} />
              <ReadOnlyField label="Email" value={ota.email} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem', marginTop: '1rem' }}>
              <ReadOnlyField label="Website" value={ota.website} />
            </div>
          </div>

          <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem' }}>Address</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
              <ReadOnlyField label="Address" value={ota.address} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
              <ReadOnlyField label="City" value={ota.city} />
              <ReadOnlyField label="State" value={ota.state} />
              <ReadOnlyField label="Country" value={ota.country} />
              <ReadOnlyField label="PIN Code" value={ota.pincode} />
            </div>
          </div>

          <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>Commission & Credit</h3>
              <CreditEligiblePill eligible={ota.isCreditEligible} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1.5rem' }}>
              <ReadOnlyField label="Commission Type" value={ota.commissionType} />
              <ReadOnlyField label="Commission %" value={formatPercent(ota.commissionPercent)} />
              <ReadOnlyField label="Credit Days" value={formatNumber(ota.creditDays)} />
              <ReadOnlyField label="Credit Limit" value={formatCurrency(ota.creditLimit)} />
            </div>
          </div>

          <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem' }}>Tax Information</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
              <ReadOnlyField label="GST Number" value={ota.gstNumber} />
              <ReadOnlyField label="PAN Number" value={ota.panNumber} />
            </div>
          </div>

          {ota.notes && (
            <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
              <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem' }}>Notes</h3>
              <div style={{ padding: '0.75rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--form-border-color)', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
                {ota.notes}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          {onEdit && (
            <button className="btn btn-primary" onClick={() => { onClose(); onEdit(ota); }}>
              <IconSystem.edit size={16} />
              Edit
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default OtaViewModal;
